/**
 * In-memory bearer-token store (task 21.1, R8.1; updated for Slice C3).
 *
 * Slice C3 switches the SPA to authenticate directly against Cognito. The value
 * held here is now the Cognito **idToken** — the JWT the per-service API Gateway
 * authorizer accepts as the `Authorization: Bearer` credential (it validates
 * `aud` = the app client id, which only the idToken carries). The function
 * names are unchanged (`getAccessToken`/`setAccessToken`/`clearAccessToken`) to
 * minimise churn in {@link ../api/client}, but the stored value is the idToken.
 *
 * The token is kept **only in memory** for the lifetime of the loaded SPA and
 * used to attach the `Authorization` header. Session persistence across reloads
 * is handled separately by the Cognito SDK (`localStorage`), which
 * {@link ./AuthContext} reads on mount via `getSession()` to repopulate this
 * store — so this module needs no persistence hooks of its own.
 */

/** The current in-memory bearer token (the Cognito idToken), or `undefined`. */
let accessToken: string | undefined;

/** Return the current in-memory bearer token (idToken), or `undefined`. */
export function getAccessToken(): string | undefined {
  return accessToken;
}

/**
 * Store the bearer token (the Cognito idToken) in memory. Passing `undefined`
 * clears it (equivalent to {@link clearAccessToken}).
 */
export function setAccessToken(token: string | undefined): void {
  accessToken = token;
}

/** Clear the in-memory bearer token (on sign-out or a lost session). */
export function clearAccessToken(): void {
  accessToken = undefined;
}
